import {
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  FitnessCenter as FitnessIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Box, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tutorials } from "../config/tutorials";
import { useAuth } from "../hooks";
import { useInteractionTracking } from "../hooks/useAnalytics";
import { formatVersionDisplay, getCurrentEnvironment, useReleaseNotes } from "../hooks/useReleaseNotes";
import { usePageTutorial } from "../hooks/useTutorial";
import { getCurrentEnvironmentName, shouldShowEnvironmentName } from "../utils/environment";

const DRAWER_WIDTH = 240;

interface SidebarProps {
  open: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get user authentication
  const { user } = useAuth();

  // Initialize interaction tracking
  const { trackClick } = useInteractionTracking("Sidebar");

  // Get release notes for version display
  const { releaseNotes } = useReleaseNotes();

  // Initialize sidebar tutorial
  usePageTutorial("sidebar_navigation_tour", tutorials.sidebar);

  const mainMenuItems = [
    {
      text: "Overview",
      icon: <DashboardIcon />,
      path: "/",
      description: "Your agenda and latest updates",
      tutorialId: "overview-nav",
    },
    {
      text: "Chat",
      icon: <ChatIcon />,
      path: "/chat",
      description: "Chat with your AI agents",
      tutorialId: "chat-nav",
    },
    {
      text: "Calendar",
      icon: <CalendarIcon />,
      path: "/calendar",
      description: "Manage your schedule and events",
      tutorialId: "calendar-nav",
    },
    {
      text: "Coaches",
      icon: <FitnessIcon />,
      path: "/coaches",
      description: "Track progress with your coaches",
      tutorialId: "coaches-nav",
    },
    {
      text: "Priorities",
      icon: <FlagIcon />,
      path: "/priorities",
      description: "Set goals and manage priorities",
      tutorialId: "priorities-nav",
    },
    {
      text: "Todo List",
      icon: <AssignmentIcon />,
      path: "/todo",
      description: "Manage your tasks and goals",
      tutorialId: "todo-nav",
    },
  ];

  const utilityMenuItems = [
    {
      text: "Profile",
      icon: <PersonIcon />,
      path: "/profile",
      description: "View and edit your profile",
      tutorialId: "profile-nav",
    },
  ];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string, itemName: string) => {
    trackClick(`navigate_to_${itemName.toLowerCase().replace(/\s+/g, "_")}`, path);
    navigate(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          backgroundColor: "#f8f9fa",
          borderRight: "1px solid #e9ecef",
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #e9ecef" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50" }}>
          Costaff.ai
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Main Menu Items */}
        <List sx={{ pt: 1, flex: 1 }}>
          {mainMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                data-tutorial={item.tutorialId}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#1976d2",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? "#1976d2" : "#6c757d",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Divider */}
        <Divider sx={{ my: 1 }} />

        {/* Utility Menu Items */}
        <List sx={{ pb: 1 }}>
          {utilityMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                data-tutorial={item.tutorialId}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#1976d2",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive(item.path) ? "#1976d2" : "#6c757d",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Display environment name and version at bottom */}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ p: 2, textAlign: "center" }}>
          {releaseNotes && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mb: 0.5 }}>
              {formatVersionDisplay(releaseNotes.currentVersion, getCurrentEnvironment())}
            </Typography>
          )}
          {shouldShowEnvironmentName() && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Environment: {getCurrentEnvironmentName()}
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
